import { useEffect, useState } from 'react';
import { api, API_BASE } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartCard } from '@/components/ChartCard';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import { Gift, TrendingUp, CheckCircle2, XCircle, Mail, Users, Download } from 'lucide-react';

const PLAN_LABEL = { try: 'Try', starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise' };

function downloadCsv() {
  const token = localStorage.getItem('ageorush_token');
  fetch(`${API_BASE}/admin/trials.csv`, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const dl = document.createElement('a');
      dl.href = URL.createObjectURL(blob);
      dl.download = `georush-trials-${new Date().toISOString().slice(0, 10)}.csv`;
      dl.click();
      URL.revokeObjectURL(dl.href);
    })
    .catch((e) => toast.error(e.message || 'Export failed'));
}

function Stat({ icon: Icon, label, value, hint, tone = 'default', testId }) {
  const toneCls = {
    default: 'text-foreground',
    accent: 'text-[hsl(var(--accent))]',
    success: 'text-emerald-600',
    danger: 'text-rose-600',
  }[tone];
  return (
    <Card className="border p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
        <Icon className={`h-4 w-4 ${toneCls}`} />
      </div>
      <div className={`mt-2 font-display text-3xl font-semibold tabular ${toneCls}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </Card>
  );
}

function statusBadge(status) {
  const s = (status || '').toLowerCase();
  if (s === 'trialing' || s === 'trial') return <Badge className="bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30">Trialing</Badge>;
  if (s === 'active' || s === 'renewed') return <Badge>Converted</Badge>;
  return <Badge variant="secondary" className="capitalize">{s.replace('_', ' ')}</Badge>;
}

export default function AdminPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/trial-metrics');
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.status === 403 ? 'Admin access required' : 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-page">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Trial funnel</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Loading metrics…</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-[var(--radius)]" />)}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4" data-testid="admin-page">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Trial funnel</h1>
        <Card className="border p-8 text-center text-sm text-muted-foreground" data-testid="admin-no-access">
          You don&apos;t have access to this page, or metrics couldn&apos;t be loaded.
        </Card>
      </div>
    );
  }

  const t = data.totals || {};
  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Trial funnel</h1>
          <p className="text-sm text-muted-foreground mt-0.5">How your 7-day free trials are converting.</p>
        </div>
        <Button variant="outline" onClick={downloadCsv} data-testid="admin-download-csv-button">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testId="admin-stat-started" icon={Users} label="Trials started" value={t.started ?? 0} hint="All-time" />
        <Stat testId="admin-stat-active" icon={Gift} label="Active trials" value={t.active_trials ?? 0} hint="In progress now" tone="accent" />
        <Stat testId="admin-stat-converted" icon={CheckCircle2} label="Converted" value={t.converted ?? 0} hint="Paying now" tone="success" />
        <Stat testId="admin-stat-lapsed" icon={XCircle} label="Lapsed" value={t.lapsed ?? 0} hint="Failed / cancelled" tone="danger" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border p-5 lg:col-span-1" data-testid="admin-conversion-card">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[hsl(var(--accent))]" />
            <div className="font-display text-base font-semibold">Conversion rate</div>
          </div>
          <div className="mt-3 font-display text-5xl font-semibold text-[hsl(var(--accent))] tabular" data-testid="admin-conversion-rate">
            {data.conversion_rate ?? 0}%
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Of {(t.converted ?? 0) + (t.lapsed ?? 0)} resolved trials, {t.converted ?? 0} converted to paid.
          </div>
        </Card>

        <Card className="border p-5 lg:col-span-2" data-testid="admin-emails-card">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <div className="font-display text-base font-semibold">Lifecycle emails sent</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ['Welcome', data.emails?.welcome_sent],
              ['Nudge (day 3)', data.emails?.nudge_sent],
              ['Ends-soon', data.emails?.reminder_sent],
              ['Ended', data.emails?.ended_sent],
            ].map(([label, val]) => (
              <div key={label} className="rounded-lg border bg-secondary/40 p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="font-display text-xl font-semibold tabular mt-1">{val ?? 0}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <ChartCard title="Conversion over time" subtitle="Weekly cohorts by trial-start date · % of that week's trials now converted" testId="admin-weekly-card">
        <div className="h-64" data-testid="admin-weekly-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.weekly || []} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="week" fontSize={11} tickFormatter={(w) => (w || '').slice(5)} />
              <YAxis fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <RTooltip
                formatter={(val, key) => (key === 'conversion_rate' ? [`${val}%`, 'Conversion'] : [val, key])}
                labelFormatter={(w) => `Week of ${w}`}
              />
              <Line type="monotone" dataKey="conversion_rate" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} name="conversion_rate" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      <Card className="border p-5" data-testid="admin-by-plan-card">
        <div className="font-display text-base font-semibold mb-3">Funnel by plan</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-by-plan-table">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr className="border-b">
                <th className="text-left py-2 font-medium">Plan</th>
                <th className="text-right py-2 font-medium">Active trials</th>
                <th className="text-right py-2 font-medium">Converted</th>
                <th className="text-right py-2 font-medium">Lapsed</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_plan || []).map((row) => (
                <tr key={row.plan} className="border-b last:border-0">
                  <td className="py-2 font-medium">{PLAN_LABEL[row.plan] || row.plan}</td>
                  <td className="text-right tabular text-[hsl(var(--accent))]">{row.active_trials}</td>
                  <td className="text-right tabular text-emerald-600">{row.converted}</td>
                  <td className="text-right tabular text-rose-600">{row.lapsed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="border p-5" data-testid="admin-recent-card">
        <div className="font-display text-base font-semibold mb-3">Recent active trials</div>
        {(data.recent_trials || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No active trials right now.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-recent-table">
              <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b">
                  <th className="text-left py-2 font-medium">User</th>
                  <th className="text-left py-2 font-medium">Plan</th>
                  <th className="text-left py-2 font-medium">Status</th>
                  <th className="text-left py-2 font-medium">Started</th>
                  <th className="text-left py-2 font-medium">Trial ends</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_trials.map((r, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2">
                      <div className="font-medium">{r.email}</div>
                      {r.name && <div className="text-xs text-muted-foreground">{r.name}</div>}
                    </td>
                    <td className="py-2">{PLAN_LABEL[r.plan] || r.plan}</td>
                    <td className="py-2">{statusBadge(r.status)}</td>
                    <td className="py-2 tabular text-muted-foreground">{r.started_at || '—'}</td>
                    <td className="py-2 tabular text-muted-foreground">{r.trial_end || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
